// Public John Tilly remembr demo — shows exactly what John sees & hears.
// No auth required. Acts as a marketing showcase + product demo in one.
import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Play, Pause, SkipBack, SkipForward, X, ArrowRight, CheckCircle, FilmReel, Sun, Sparkle, Shuffle, Hourglass, MoonStars } from "@phosphor-icons/react";
import { ForgetMeNotIcon } from "../components/ForgetMeNotIcon";
import { VolumeControl } from "../components/VolumeControl";
import { NarratorIntroCard } from "../components/NarratorIntroCard";
import { SessionControls } from "../components/SessionControls";
import { RemixLoopCTA } from "../components/RemixLoopCTA";
import { useVisitorToken } from "../lib/useVisitorToken";
import { useAudioStabilizer } from "../lib/useAudioStabilizer";
import { API } from "../lib/api";
import { toast } from "sonner";

const SILENT_DURATION_MS = 9000;

// Use a plain axios instance — no credentials, no cookies, this is fully public.
const publicApi = axios.create({ baseURL: API, withCredentials: false });

function fileUrl(fileId) {
    return `${API}/demo/files/${fileId}/download`;
}

export default function JohnsDemo() {
    const { t, i18n } = useTranslation();
    // Route may be /demo/john (= john-tilly) or /demo/ella (= ella-jenkins).
    // We map the friendly URL → backend slug here. Jordy was retired Feb 2026
    // (per Glenn — John + Ella are sufficient demos; saves narration cost) —
    // any /demo/jordy hits now gracefully fall back to Ella.
    const params = useParams();
    const routeSlug = params.slug || "john";
    const SLUG_MAP = { john: "john-tilly", ella: "ella-jenkins", "john-tilly": "john-tilly", "ella-jenkins": "ella-jenkins", jordy: "ella-jenkins", "jordy-bennett": "ella-jenkins" };
    const demoSlug = SLUG_MAP[routeSlug] || "john-tilly";
    // Theme swap: kids demos (Ella) wear soft pink + baby-blue tones;
    // adult demos (John) keep the cinematic dark-navy palette.
    const isKidsDemo = demoSlug === "ella-jenkins";
    const T = isKidsDemo
        ? {
            bg: "bg-[#FDF2F4]",                     // soft dawn pink wash
            bgRgb: "#FDF2F4",
            text: "text-[#3A2C3A]",                 // deep plum-near-black
            subText: "text-[#6B5560]",
            eyebrow: "text-[#7E96C0]",              // dusty baby blue
            titleAccent: "text-[#D88AAB]",          // soft rose
            cardBg: "bg-white/85",
            cardBorder: "border-[#F4D5DD]",
            controlBg: "bg-white",
            controlHoverBg: "hover:bg-[#F4D5DD]/50",
            controlText: "text-[#D88AAB]",
            ctaBg: "bg-[#D88AAB] hover:bg-[#C57A9C] text-white",
            captionTone: "text-[#3A2C3A]/80",
            gradientFrom: "from-[#FDF2F4]",         // controls fade for kids
            iconAccent: "text-[#7E96C0]",           // baby blue
            progressDim: "bg-[#3A2C3A]/15",
            progressActive: "bg-[#D88AAB]",
            progressPast: "bg-[#D88AAB]/40",
            modalBg: "bg-[#FDF2F4]",
            modalBorder: "border-[#F4D5DD]",
            modalText: "text-[#3A2C3A]",
            inputBg: "bg-white",
            inputBorder: "border-[#F4D5DD]",
            inputText: "text-[#3A2C3A]",
            inputPlaceholder: "placeholder:text-[#3A2C3A]/30",
            closeBg: "bg-white/80 hover:bg-white",
            closeText: "text-[#3A2C3A]",
        }
        : {
            bg: "bg-[#1a1c25]",
            bgRgb: "#1a1c25",
            text: "text-white",
            subText: "text-white/75",
            eyebrow: "text-[#A8B5E0]/80",
            titleAccent: "text-[#A8B5E0]",
            cardBg: "bg-white/10",
            cardBorder: "border-white/15",
            controlBg: "bg-white",
            controlHoverBg: "hover:bg-[#F4F6FB]",
            controlText: "text-[#4A5A95]",
            ctaBg: "bg-white text-[#4A5A95] hover:bg-[#F4F6FB]",
            captionTone: "text-white/80",
            gradientFrom: "from-[#1a1c25]",
            iconAccent: "text-[#F4D068]",
            progressDim: "bg-white/15",
            progressActive: "bg-white",
            progressPast: "bg-white/40",
            modalBg: "bg-[#1f2230]",
            modalBorder: "border-white/10",
            modalText: "text-white",
            inputBg: "bg-white/5",
            inputBorder: "border-white/15",
            inputText: "text-white",
            inputPlaceholder: "placeholder:text-white/30",
            closeBg: "bg-white/10 hover:bg-white/20",
            closeText: "text-white",
        };
    const sourceTag = `${demoSlug}_demo_end`;
    const [playlist, setPlaylist] = useState([]);
    const [profile, setProfile] = useState(null);
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(true);   // start paused — need user gesture for audio autoplay
    const [started, setStarted] = useState(false); // shows intro overlay until first START click
    const [loading, setLoading] = useState(true);
    const [audioReady, setAudioReady] = useState({ total: 0, with_audio: 0 });

    // Narrator intros — { [contributor_name]: { display_name, relationship, intro_audio_file_id, ... } }
    // Slot a personal "Hi John, it's Margaret…" card before each narrator's first cluster of memories.
    const [narrators, setNarrators] = useState({});
    const [introNarrator, setIntroNarrator] = useState(null); // null when no intro is playing
    const introShownRef = useRef(new Set()); // track which narrators have already had their intro this session
    // Tracks the PREVIOUS value of introNarrator so we can detect the moment
    // the intro card closes and rewind the main story audio to its start.
    const prevIntroNarratorRef = useRef(null);

    // End-of-demo email capture
    const [endEmail, setEndEmail] = useState("");
    const [endRelation, setEndRelation] = useState("");
    const [endSubmitting, setEndSubmitting] = useState(false);
    const [endSaved, setEndSaved] = useState(false);

    // Media embed (YouTube clip pill + modal)
    const [embedOpen, setEmbedOpen] = useState(false);
    // "Families upload their playlist" — real form (Spotify/YouTube playlist URL capture)
    const [playlistTeaseOpen, setPlaylistTeaseOpen] = useState(false);
    const [playlistUrl, setPlaylistUrl] = useState("");
    const [playlistName, setPlaylistName] = useState("");
    const [playlistEmail, setPlaylistEmail] = useState("");
    const [playlistSubmitting, setPlaylistSubmitting] = useState(false);
    const [playlistSaved, setPlaylistSaved] = useState(false);
    const [playlistError, setPlaylistError] = useState(null);

    const audioRef = useRef(null);
    const silentTimerRef = useRef(null);
    // Transient-failure retry counters — a preview-server wake-up or a wifi
    // blip used to fire <audio onError> on every remaining memory in turn,
    // chain-skipping whole chapters in seconds (seen on Ella's demo as
    // "Nick → blank page → Ella speaks"). We now retry with backoff before
    // ever giving up on a memory.
    const audioRetryRef = useRef(0);
    const photoRetryRef = useRef({});

    // Session mode (Today's, Themed, Shuffle, Era, Lights On) — driven by URL
    // query string. Distributors can deep-link e.g. /demo/john?mode=lights-on
    // to showcase a particular play mode in a pitch.
    const [searchParams] = useSearchParams();
    const urlMode = searchParams.get("mode") || "full";
    const sessionTheme = searchParams.get("theme") || null;
    const sessionEra = searchParams.get("era") || null;
    // Smart Shuffle — caregivers pick a session style + a length cap from
    // the intro splash before pressing START. Defaults preserve the
    // existing curated journey unless the URL query string overrides them.
    const [shuffleStyle, setShuffleStyle] = useState(
        ["full", "smart", "chronological"].includes(urlMode) ? urlMode : "full"
    );
    const [capMinutes, setCapMinutes] = useState(
        Number(searchParams.get("cap_minutes")) || null
    );
    // Combined mode used by the playlist fetch: URL-driven session mode
    // (themed / era / lights-on / today) takes precedence over the
    // splash-driven shuffleStyle (full / smart / chronological).
    const sessionMode = (urlMode && !["full", "smart", "chronological"].includes(urlMode))
        ? urlMode
        : shuffleStyle;
    // Estimated total session length — set by the playlist response so the
    // splash can show "~12 min · 23 memories" beneath the START button.
    const [sessionStats, setSessionStats] = useState({ sec: null, count: null });

    // "Saturday morning re-mix" loop — anonymous browser ID + the active
    // session_id so we can record what was actually heard at session end.
    // On a fresh play (after the user has just finished a session), we ask
    // the backend for fresh=true and a new seed so they hear different
    // memories than last time.
    const visitorToken = useVisitorToken();
    const [activeSession, setActiveSession] = useState(null); // { id, seed }
    const [sessionSeed, setSessionSeed] = useState(null);     // forwarded into the playlist call
    const [freshPass, setFreshPass] = useState(false);        // true after the first "Play another" click
    const playedMemoryIdsRef = useRef(new Set());             // memory ids heard during current session

    // 1. ensure() then load playlist
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                await publicApi.post(`/demo/${demoSlug}/ensure`);
                const params = {};
                if (sessionMode && sessionMode !== "full") params.mode = sessionMode;
                if (sessionTheme) params.theme = sessionTheme;
                if (sessionEra)   params.era   = sessionEra;
                if (capMinutes && capMinutes > 0) params.cap_minutes = capMinutes;
                if (sessionSeed !== null) params.seed = sessionSeed;
                if (freshPass && visitorToken) {
                    params.fresh = true;
                    params.visitor_token = visitorToken;
                }
                const { data } = await publicApi.get(`/demo/${demoSlug}/playlist`, { params });
                if (!active) return;
                setProfile(data.profile);
                // Surface session stats to the splash + the playing UI so caregivers
                // know how long their chosen session will last.
                setSessionStats({
                    sec: data?.estimated_duration_sec ?? null,
                    count: data?.memory_count ?? (Array.isArray(data?.playlist) ? data.playlist.length : null),
                });

                // Fetch narrator intros in parallel — used to slot a personal
                // "Hi John, it's Margaret…" card before each narrator's first
                // memory cluster. Failure here is non-fatal — the player works
                // without intros if the API can't reach the narrators endpoint.
                try {
                    const narResp = await publicApi.get(`/demo/${demoSlug}/narrators`);
                    if (active && narResp?.data?.narrators) {
                        setNarrators(narResp.data.narrators);
                    }
                } catch (e) {
                    // Soft-fail — keep playing without intros.
                    console.warn("Narrator intros unavailable:", e?.message);
                }
                // If the family has uploaded a custom welcome (audio + optional
                // photo + optional caption), prepend it as a synthetic first
                // memory so the player opens with the family's own voice
                // before the AI-narrated session begins.
                const list = data.playlist || [];
                if (data.profile && data.profile.welcome_audio_file_id) {
                    // Hand the locale-aware audio file id (translated welcome
                    // if available, else the original) to the synthetic
                    // memory as audio_translations so the existing locale
                    // selection logic in the player picks it up automatically.
                    const welcomeMemory = {
                        id: `${data.profile.id}-custom-welcome`,
                        profile_id: data.profile.id,
                        category_slug: "welcome",
                        title: data.profile.welcome_caption || `Hello, ${data.profile.subject_name_short || ""}`.trim(),
                        story: null,
                        contributor_name: "from the family",
                        photo_file_id: data.profile.welcome_photo_file_id || null,
                        audio_file_id: data.profile.welcome_audio_file_id,
                        audio_skip: false,
                        audio_translations: data.profile.welcome_audio_translations || {},
                        title_translations: {},
                        story_translations: {},
                        media_embed: null,
                        video_file_id: null,
                        sort_year: null,
                        is_custom_welcome: true,
                    };
                    setPlaylist([welcomeMemory, ...list]);
                } else {
                    setPlaylist(list);
                }
            } catch (e) {
                // Even if ensure fails, try playlist
                try {
                    const params = {};
                    if (sessionMode && sessionMode !== "full") params.mode = sessionMode;
                    if (sessionTheme) params.theme = sessionTheme;
                    if (sessionEra)   params.era   = sessionEra;
                    if (capMinutes && capMinutes > 0) params.cap_minutes = capMinutes;
                    if (sessionSeed !== null) params.seed = sessionSeed;
                    if (freshPass && visitorToken) {
                        params.fresh = true;
                        params.visitor_token = visitorToken;
                    }
                    const { data } = await publicApi.get(`/demo/${demoSlug}/playlist`, { params });
                    if (!active) return;
                    setProfile(data.profile);
                    setPlaylist(data.playlist || []);
                } catch { /* leave empty */ }
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [demoSlug, sessionMode, sessionTheme, sessionEra, capMinutes, sessionSeed, freshPass, visitorToken]);

    // 2. Poll narration status while audio is still being generated
    useEffect(() => {
        if (!started) return;
        let cancelled = false;
        let timer = null;
        const tick = async () => {
            try {
                const { data } = await publicApi.get(`/demo/${demoSlug}/status`);
                if (cancelled) return;
                setAudioReady({ total: data.total, with_audio: data.with_audio });
                if (!data.ready) timer = setTimeout(tick, 6000);
                else {
                    // Refresh playlist once everything's ready so newly-generated audio_file_ids are loaded
                    const params = {};
                    if (sessionMode && sessionMode !== "full") params.mode = sessionMode;
                    if (sessionTheme) params.theme = sessionTheme;
                    if (sessionEra)   params.era   = sessionEra;
                    if (capMinutes && capMinutes > 0) params.cap_minutes = capMinutes;
                    if (sessionSeed !== null) params.seed = sessionSeed;
                    if (freshPass && visitorToken) {
                        params.fresh = true;
                        params.visitor_token = visitorToken;
                    }
                    const { data: pl } = await publicApi.get(`/demo/${demoSlug}/playlist`, { params });
                    if (!cancelled) setPlaylist(pl.playlist || []);
                }
            } catch {
                timer = setTimeout(tick, 8000);
            }
        };
        tick();
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, [started, sessionMode, sessionTheme, sessionEra, capMinutes, sessionSeed, freshPass, visitorToken]);

    const current = playlist[idx];
    const nextMem = playlist[idx + 1] || null;

    // Lifted up next to `current` so the WebAudio stabiliser hook can subscribe
    // — audioSrc *also* feeds the <audio key={audioSrc}> further down, so this
    // is a single source of truth.
    const lang = i18n.language;
    const translatedAudioId = current?.audio_translations && current.audio_translations[lang];
    const audioSrc = translatedAudioId
        ? fileUrl(translatedAudioId)
        : (current?.audio_file_id ? fileUrl(current.audio_file_id) : null);

    // Volume stabiliser + caregiver-facing volume control. Same default preset
    // ("comfortable" / 75%) as the dementia-mode ReminiscePlayer so the level
    // is consistent the moment a user moves from one demo to the other.
    const { volume, preset, setVolume, setPreset } = useAudioStabilizer(audioRef, audioSrc);

    // Dramatic chapter-transition overlay — fades in over the final 4s of the
    // current memory's audio whenever the NEXT memory opens a new chapter that
    // we want to mark cinematically (see CHAPTER_LABELS below). Cleared at
    // memory boundaries by the [idx] effect lower down.
    const [chapterOverlay, setChapterOverlay] = useState(null);
    const CHAPTER_LABELS = {
        "chapter-two": { eyebrow: "Chapter Two", title: "Primary school years", years: "ages 5 — 12" },
        "chapter-three": { eyebrow: "Chapter Three", title: "High school & university", years: "ages 13 — 22" },
        "handover": { eyebrow: "The handover", title: "A graduation gift", years: "Mum & Dad → Ella" },
        "to-be-continued": { eyebrow: "Epilogue", title: "Ella speaks", years: "ages 22 → ∞" },
    };
    // Detect a chapter boundary purely from the playlist: the next memory's
    // category_slug differs from this one AND it sits in CHAPTER_LABELS.
    const nextChapterKey = (nextMem && nextMem.category_slug !== current?.category_slug && CHAPTER_LABELS[nextMem.category_slug])
        ? nextMem.category_slug
        : null;

    // Skip-to-next-chapter target — for revisiting viewers who want to jump
    // ahead. Scans forward from the current memory and finds the first one
    // whose category_slug is a known chapter boundary (chapter-two,
    // chapter-three, handover). Returns null if there's nothing left to skip
    // (e.g. the user is already in the final chapter).
    let nextChapterTarget = null;
    if (current && playlist.length) {
        for (let i = idx + 1; i < playlist.length; i++) {
            const m = playlist[i];
            if (CHAPTER_LABELS[m.category_slug] && m.category_slug !== current.category_slug) {
                nextChapterTarget = { idx: i, key: m.category_slug, label: CHAPTER_LABELS[m.category_slug] };
                break;
            }
        }
    }
    const skipToNextChapter = () => {
        if (!nextChapterTarget) return;
        const fromIdx = idx;
        const chapterName = t(`demo.chapters.${nextChapterTarget.key}`, { defaultValue: nextChapterTarget.label.eyebrow });
        setChapterOverlay(null);
        setHandoverHold(null);
        setIdx(nextChapterTarget.idx);
        setPaused(false);
        // A chapter jump silently passes over every memory in between — make
        // that explicit and reversible so an accidental tap never reads as
        // "the demo is missing content".
        toast(t("demo.skipped_to", { chapter: chapterName }), {
            action: { label: t("demo.skip_undo"), onClick: () => { setIdx(fromIdx); setPaused(false); } },
            duration: 6000,
        });
    };

    const advance = useCallback(() => {
        setIdx((i) => (i < playlist.length - 1 ? i + 1 : i));
    }, [playlist.length]);

    const goBack = useCallback(() => {
        setIdx((i) => (i > 0 ? i - 1 : 0));
    }, []);

    // Photo album cycling — when a memory has multiple photos (cover +
    // photo_file_ids[]), rotate through them every ~4 seconds while the
    // audio is playing. Reset to the cover at every memory boundary.
    const [albumIdx, setAlbumIdx] = useState(0);
    useEffect(() => { setAlbumIdx(0); }, [current?.id]);

    // Timed mid-narration photo switch — one-way swap to photo_switch.file_id
    // once the audio passes photo_switch.at_seconds (e.g. Margaret's sea-change
    // narration cuts to Lea's salon when the hairdressing ritual is mentioned).
    const [photoSwitched, setPhotoSwitched] = useState(false);
    useEffect(() => { setPhotoSwitched(false); }, [current?.id]);
    useEffect(() => {
        // Pre-warm the switch photo so the swap is instant.
        const fid = current?.photo_switch?.file_id;
        if (fid) { const im = new Image(); im.src = fileUrl(fid); }
    }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        const extras = (current?.photo_file_ids || []).filter(Boolean);
        if (extras.length === 0 || paused) return;
        const interval = setInterval(() => {
            setAlbumIdx((i) => (i + 1) % (extras.length + 1));
        }, 4000);
        return () => clearInterval(interval);
    }, [current?.id, current?.photo_file_ids, paused]);

    // Track whether the current audio is buffering (slow network / large
    // file still downloading) so we can show a subtle spinner over the
    // bottom controls. Cleared whenever audio is actively playing.
    const [audioBuffering, setAudioBuffering] = useState(false);
    useEffect(() => { setAudioBuffering(false); }, [current?.id]);

    // Prefetch the NEXT memory's photo + audio while the current one plays.
    // This eliminates the 1–3s blank pause that slower connections suffer
    // when each file only starts downloading at advance() time.
    //
    // IMPORTANT: prefetching via `fetch()` creates a separate cache entry that
    // doesn't share identity with the audio element's Range requests, so the
    // file ends up downloaded twice. We instead prefetch via hidden DOM
    // elements (`Image` and `Audio`) — same cache key, same Range semantics,
    // genuine cache reuse.
    const prefetchRef = useRef({});
    useEffect(() => {
        // Prefetch the NEXT TWO memories so slow connections still feel
        // gap-free even when the user taps Next rapidly through chapters.
        const upcoming = [playlist[idx + 1], playlist[idx + 2]].filter(Boolean);
        if (upcoming.length === 0) return;
        const audioIds = [];
        const photoIds = [];
        upcoming.forEach((m) => {
            if (m.audio_file_id) audioIds.push(m.audio_file_id);
            const trAudio = m.audio_translations?.[i18n.language];
            if (trAudio) audioIds.push(trAudio);
            if (m.photo_file_id) photoIds.push(m.photo_file_id);
            (m.photo_file_ids || []).forEach((id) => photoIds.push(id));
        });
        // Photos: Image() shares cache with <img src=...>.
        photoIds.forEach((fid) => {
            const img = new Image();
            img.decoding = "async";
            img.fetchPriority = "low";
            img.src = fileUrl(fid);
            prefetchRef.current[`img-${fid}`] = img;
        });
        // Audio: hidden <audio preload="auto"> matches Range-request semantics
        // of the real player element so the cache hit is exact.
        audioIds.forEach((fid) => {
            const key = `aud-${fid}`;
            if (prefetchRef.current[key]) return;
            const a = new Audio();
            a.preload = "auto";
            a.src = fileUrl(fid);
            try { a.load(); } catch (_) { /* no-op */ }
            prefetchRef.current[key] = a;
        });
    }, [idx, playlist, i18n.language]);

    // If a photo fails to load (same transient-outage class of problem as the
    // audio retries above), retry it twice with backoff + cache-bust instead
    // of leaving a blank frame behind the title text.
    const handlePhotoError = (e) => {
        const img = e.currentTarget;
        const fid = img.dataset.fid;
        if (!fid) return;
        const tries = photoRetryRef.current[fid] || 0;
        if (tries >= 2) return;
        photoRetryRef.current[fid] = tries + 1;
        setTimeout(() => { img.src = `${fileUrl(fid)}?retry=${tries + 1}`; }, 1200 * (tries + 1));
    };

    // Prefetch the skip-pill target's cover photo so a chapter jump lands on
    // a ready frame instead of a blank one.
    useEffect(() => {
        if (!nextChapterTarget) return;
        const m = playlist[nextChapterTarget.idx];
        if (m?.photo_file_id && !prefetchRef.current[`img-${m.photo_file_id}`]) {
            const img = new Image();
            img.decoding = "async";
            img.fetchPriority = "low";
            img.src = fileUrl(m.photo_file_id);
            prefetchRef.current[`img-${m.photo_file_id}`] = img;
        }
    }, [nextChapterTarget?.idx, playlist]);

    // Prefetch the NEXT 3 memory photos as the demo advances so each
    // transition lands on a ready frame. Glenn reported the opening
    // around-the-table and Margaret family photos felt "clunky / up for
    // 1 second" — root cause was the 3 s silent-advance timer firing
    // before the photo bytes arrived. Combined with the photo-decode-
    // gated silent timer above, this gives every memory a fully decoded
    // image on screen before the user can register the transition.
    useEffect(() => {
        if (playlist.length === 0) return;
        for (let lookahead = 0; lookahead <= 3; lookahead++) {
            const m = playlist[idx + lookahead];
            if (m?.photo_file_id && !prefetchRef.current[`img-${m.photo_file_id}`]) {
                const img = new Image();
                img.decoding = "async";
                img.fetchPriority = lookahead === 0 ? "high" : "low";
                img.src = fileUrl(m.photo_file_id);
                prefetchRef.current[`img-${m.photo_file_id}`] = img;
            }
        }
    }, [idx, playlist]);

    // Prefetch EVERY narrator's intro photo + video + audio the moment the
    // narrators payload arrives. Without this, the first time a contributor's
    // memory comes up the NarratorIntroCard mounts with an empty portrait,
    // then the photo loads progressively, then the muted video crossfades in
    // — feels janky and breaks the emotional rhythm. Pre-warming the cache
    // means the card always opens on a ready frame with the video already
    // buffered (true cache hit since we use the same DOM-element fetch path
    // the card itself uses → identical Range-request semantics).
    useEffect(() => {
        const entries = Object.values(narrators || {});
        if (entries.length === 0) return;
        entries.forEach((n) => {
            // Photo — Image() shares cache with <img src=...>
            if (n?.intro_photo_file_id && !prefetchRef.current[`nar-img-${n.intro_photo_file_id}`]) {
                const img = new Image();
                img.decoding = "async";
                img.fetchPriority = "high"; // narrator cards need to land instantly
                img.src = fileUrl(n.intro_photo_file_id);
                prefetchRef.current[`nar-img-${n.intro_photo_file_id}`] = img;
            }
            // Video — hidden <video preload="auto"> matches the same Range
            // semantics the NarratorIntroCard's real <video> element uses.
            if (n?.intro_video_file_id && !prefetchRef.current[`nar-vid-${n.intro_video_file_id}`]) {
                const v = document.createElement("video");
                v.preload = "auto";
                v.muted = true;
                v.src = fileUrl(n.intro_video_file_id);
                try { v.load(); } catch (_) { /* no-op */ }
                prefetchRef.current[`nar-vid-${n.intro_video_file_id}`] = v;
            }
            // Audio — same trick for the narrator's "Hi John, it's Margaret…"
            // greeting so the timing source-of-truth doesn't stall either.
            if (n?.intro_audio_file_id && !prefetchRef.current[`nar-aud-${n.intro_audio_file_id}`]) {
                const a = new Audio();
                a.preload = "auto";
                a.src = fileUrl(n.intro_audio_file_id);
                try { a.load(); } catch (_) { /* no-op */ }
                prefetchRef.current[`nar-aud-${n.intro_audio_file_id}`] = a;
            }
        });
    }, [narrators]);

    // Cinematic finale — when the LAST memory's audio finishes, show a
    // 3-second fade-to-black hold with a typographic line before the
    // email-capture CTA reads. Only fires for the handover memory.
    const [handoverHold, setHandoverHold] = useState(null);
    // Gate the end-of-demo CTA so it only appears AFTER the closing
    // narration (and the handover fade-to-black) finishes — otherwise the
    // frosted card sits over the title/contributor area for the entire
    // 30-second handover memory.
    const [endCtaVisible, setEndCtaVisible] = useState(false);
    const showHandoverHold = () => {
        // The dramatic fade-to-black finale now lives on the "to-be-continued"
        // epilogue (Ella speaking herself), not the parents' handover memory.
        if (current?.category_slug !== "to-be-continued") return;
        const line = profile?.subject_name_short
            ? `${profile.subject_name_short}'s story — to be continued.`
            : "To be continued.";
        setHandoverHold(line);
        setTimeout(() => {
            setHandoverHold(null);
            // Reveal the email-capture card as the black overlay fades out
            // — perfectly timed so the CTA "arrives" with the silence.
            setEndCtaVisible(true);
        }, 3200);
    };

    // Close any open YouTube embed when memory changes
    useEffect(() => { setEmbedOpen(false); setChapterOverlay(null); setHandoverHold(null); setEndCtaVisible(false); audioRetryRef.current = 0; }, [idx]);

    // Pause narration while the YouTube modal is open
    useEffect(() => {
        if (!audioRef.current) return;
        if (embedOpen) audioRef.current.pause();
        else if (!paused) audioRef.current.play().catch(() => {});
    }, [embedOpen, paused]);

    // Auto-advance: when audio ends OR after SILENT_DURATION_MS if no audio,
    // PLUS a safety timer that always advances after 90s so we can never stall
    // mid-demo if the browser blocks autoplay or an audio file fails to load.
    useEffect(() => {
        if (!started || paused || !current || embedOpen || introNarrator) return;
        if (silentTimerRef.current) { clearTimeout(silentTimerRef.current); silentTimerRef.current = null; }
        // Fallback path for the final memory — reveals the CTA via the same
        // gate the audio-onEnded handler uses, in case the audio fails or the
        // memory has no audio at all.
        const finalizeAndAdvance = () => {
            if (atEnd) {
                if (current?.category_slug === "to-be-continued") showHandoverHold();
                else setEndCtaVisible(true);
            }
            advance();
        };
        if (current.audio_file_id && audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => { /* user gesture missing — they'll press play */ });
            // Belt-and-braces: if audio never fires `ended` (broken file, codec issue,
            // muted autoplay), still advance after the audio's *actual* duration
            // plus a 5-second buffer. We schedule once the metadata is loaded so
            // long narrations (e.g. 2-3 minutes) aren't truncated by a static cap.
            // Initial fallback of 180s is used until metadata reports duration,
            // then we reschedule with the real value.
            const scheduleSafety = (ms) => {
                if (silentTimerRef.current) clearTimeout(silentTimerRef.current);
                silentTimerRef.current = setTimeout(finalizeAndAdvance, ms);
            };
            scheduleSafety(45000);
            const onMeta = () => {
                const d = audioRef.current?.duration;
                if (d && isFinite(d) && d > 0) {
                    scheduleSafety(d * 1000 + 5000);
                }
            };
            // If duration already known (cached audio), use it immediately.
            const knownDur = audioRef.current.duration;
            if (knownDur && isFinite(knownDur) && knownDur > 0) {
                scheduleSafety(knownDur * 1000 + 5000);
            } else {
                audioRef.current.addEventListener("loadedmetadata", onMeta, { once: true });
            }
            // Clean up the metadata listener if the effect re-runs before it fires.
            const cleanupMetaListener = () => {
                audioRef.current?.removeEventListener("loadedmetadata", onMeta);
            };
            // Stash the cleanup so the return-statement below can call it too.
            silentTimerRef.cleanup = cleanupMetaListener;
        } else {
            // Silent establishing frames (audio_skip=true) — give the viewer
            // a real 5-second beat to register the photo (was 3 s — too snappy
            // when the photo bytes hadn't arrived yet, so Glenn was only
            // seeing the image for ~1 second before auto-advance fired).
            // We ALSO wait for the photo to actually decode before kicking
            // off the timer; otherwise a slow-network user sees nothing
            // meaningful before the auto-advance jumps to the next memory.
            const dur = current.audio_skip ? 5000 : SILENT_DURATION_MS;
            const photoId = current.photo_file_id;
            const startSilentTimer = () => {
                silentTimerRef.current = setTimeout(finalizeAndAdvance, dur);
            };
            if (photoId) {
                const probe = new Image();
                let started = false;
                const onProbeDone = () => {
                    if (started) return;
                    started = true;
                    startSilentTimer();
                };
                probe.onload = onProbeDone;
                probe.onerror = onProbeDone; // fail-open so a broken photo never strands the demo
                probe.src = `${API}/demo/files/${photoId}/download`;
                if (probe.complete && probe.naturalWidth > 0) onProbeDone();
                // Hard 4 s ceiling so a stuck CDN never strands the demo either.
                silentTimerRef.cleanup = (() => {
                    const safety = setTimeout(onProbeDone, 4000);
                    return () => { clearTimeout(safety); probe.onload = null; probe.onerror = null; };
                })();
            } else {
                startSilentTimer();
            }
        }
        return () => {
            if (silentTimerRef.current) clearTimeout(silentTimerRef.current);
            if (silentTimerRef.cleanup) { silentTimerRef.cleanup(); silentTimerRef.cleanup = null; }
        };
        // depend on the memory id (stable string) rather than the object reference,
        // so polling-driven playlist refreshes don't re-fire the timer mid-frame and
        // cause a visible "double stutter" on the intro slide.
        // `introNarrator` is included so the safety timer is freshly scheduled
        // the moment a narrator intro card closes — otherwise a stale 45 s
        // safety from before the intro was triggered could either kill the
        // story early or fail to fire at all, leaving the player stuck.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [current?.id, current?.audio_file_id, paused, advance, started, embedOpen, introNarrator]);

    useEffect(() => {
        if (!audioRef.current) return;
        if (paused || introNarrator) {
            audioRef.current.pause();
        } else {
            // When the narrator intro card has just dismissed, rewind the
            // main story audio to 0 — without this, the ~50–500 ms of audio
            // that played BEFORE the intro card mounted (race between the
            // current-id change and the intro trigger) would silently roll
            // forward, so the story narration would feel "cut off short at
            // the start" once the intro completes.
            if (prevIntroNarratorRef.current && !introNarrator) {
                try { audioRef.current.currentTime = 0; } catch (_) { /* ignore */ }
            }
            audioRef.current.play().catch(() => {});
        }
        prevIntroNarratorRef.current = introNarrator;
    }, [paused, introNarrator]);

    // Stall watchdog — the safety timer above only covers "metadata never
    // arrived"; this covers MID-STREAM hangs (request stalls while the server
    // sleeps → currentTime freezes, spinner spins forever, no error fires).
    // If playback time stops moving for ~12s we reload-and-resume the element;
    // if it's still frozen at ~24s we move on to the next memory.
    useEffect(() => {
        if (!started || paused || embedOpen || !current?.audio_file_id || introNarrator) return;
        let lastTime = -1;
        let stalls = 0;
        const iv = setInterval(() => {
            const a = audioRef.current;
            if (!a || a.paused) return; // user-paused / between elements
            if (a.currentTime > lastTime + 0.05) {
                lastTime = a.currentTime;
                stalls = 0;
                return;
            }
            stalls += 1;
            if (stalls === 3) {
                setAudioBuffering(true);
                const resumeAt = a.currentTime;
                try {
                    a.load();
                    a.addEventListener("loadedmetadata", () => {
                        try { a.currentTime = resumeAt; } catch (_) { /* restart from 0 */ }
                    }, { once: true });
                    a.play().catch(() => {});
                } catch (_) { /* next tick will escalate */ }
            } else if (stalls >= 6) {
                advance();
            }
        }, 4000);
        return () => clearInterval(iv);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [current?.id, started, paused, embedOpen, advance]);

    function handleStart() {
        setStarted(true);
        setPaused(false);
        setIdx(0);
    }

    // Open a tracked session whenever `started` flips true (initial click
    // OR "Play another session" replay). Sending this via an effect (not
    // handleStart) means the second-pass replay path is symmetric with
    // the first-pass path — both get a /sessions/begin POST.
    useEffect(() => {
        if (!started || !visitorToken || activeSession?.id) return;
        playedMemoryIdsRef.current = new Set();
        const planned = (playlist || []).map((m) => m.id).filter(Boolean);
        let cancelled = false;
        publicApi
            .post(`/demo/${demoSlug}/sessions/begin`, {
                visitor_token: visitorToken,
                mode: sessionMode,
                cap_minutes: capMinutes || null,
                seed: sessionSeed,
                fresh: freshPass,
                memory_ids_planned: planned,
            })
            .then(({ data }) => {
                if (!cancelled) setActiveSession({ id: data.session_id, seed: data.seed });
            })
            .catch(() => { /* swallow — non-critical telemetry */ });
        return () => { cancelled = true; };
    // We intentionally do NOT depend on playlist here — we only want this
    // effect to fire on the started transition, not every playlist update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [started, visitorToken, activeSession?.id]);

    // Track which memory IDs are actually heard this session. The set is
    // sent back to the server at session-end so future "fresh" playlists
    // can deprioritise these. Memory must be CURRENT (visible) to count.
    useEffect(() => {
        if (!started || !current?.id) return;
        playedMemoryIdsRef.current.add(current.id);
    }, [started, current?.id]);

    // Persist the played set the moment the session ends (either the
    // player hits the end of the playlist OR the visitor closes the tab).
    const closeSessionOnServer = useCallback(async (completed = true) => {
        if (!activeSession?.id || !visitorToken) return;
        try {
            await publicApi.post(
                `/demo/${demoSlug}/sessions/${activeSession.id}/end`,
                {
                    visitor_token: visitorToken,
                    memory_ids_played: Array.from(playedMemoryIdsRef.current),
                    is_completed: !!completed,
                }
            );
        } catch { /* swallow */ }
    }, [activeSession?.id, visitorToken, demoSlug]);

    // Close on tab unload — best-effort, browsers may drop it but worth a shot.
    useEffect(() => {
        if (!activeSession?.id) return;
        const onUnload = () => {
            try {
                const body = JSON.stringify({
                    visitor_token: visitorToken,
                    memory_ids_played: Array.from(playedMemoryIdsRef.current),
                    is_completed: false,
                });
                navigator.sendBeacon?.(`${API}/demo/${demoSlug}/sessions/${activeSession.id}/end`,
                    new Blob([body], { type: "application/json" }));
            } catch { /* swallow */ }
        };
        window.addEventListener("beforeunload", onUnload);
        return () => window.removeEventListener("beforeunload", onUnload);
    }, [activeSession?.id, visitorToken, demoSlug]);

    // "Play another session" — close the current session, mint a new seed,
    // flip the fresh flag, reset the player to idx=0. The playlist useEffect
    // upstream will re-fetch with fresh=true + the new seed.
    const playAnotherSession = useCallback(async () => {
        await closeSessionOnServer(true);
        setActiveSession(null);
        setSessionSeed(Math.floor(Math.random() * 2_000_000_000));
        setFreshPass(true);
        setIdx(0);
        setStarted(false); // brief flash back to splash so the new playlist hydrates cleanly
        setEndCtaVisible(false);
        setEndSaved(false);
        setTimeout(() => {
            setStarted(true);
            setPaused(false);
        }, 350);
    }, [closeSessionOnServer]);

    // Persist the played set the moment the demo naturally ends — independent
    // of whether the caregiver clicks "Play another" or just closes the tab.
    // Without this, the recently-played memories would never reach the
    // freshness filter and the re-mix loop would feel pointless.
    const endedFlagRef = useRef(false);
    useEffect(() => {
        if (endCtaVisible && activeSession?.id && !endedFlagRef.current) {
            endedFlagRef.current = true;
            closeSessionOnServer(true);
        }
        if (!endCtaVisible) endedFlagRef.current = false;
    }, [endCtaVisible, activeSession?.id, closeSessionOnServer]);

    // Narrator-intro trigger: when the player reaches a memory whose
    // contributor has a narrator intro AND we haven't shown that contributor's
    // intro yet this session, slot in the personal "Hi John, it's Margaret…"
    // card before the memory's audio starts. The card pauses the main audio
    // via the introNarrator gate baked into the play/pause useEffects above.
    useEffect(() => {
        if (!started || !current || introNarrator) return;
        const contributorName = current.contributor_name;
        if (!contributorName) return;
        const narrator = narrators[contributorName];
        if (!narrator || !narrator.intro_audio_file_id) return;
        if (introShownRef.current.has(contributorName)) return;
        // First time we see this narrator in this session — fire the intro card.
        introShownRef.current.add(contributorName);
        setIntroNarrator(narrator);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [current?.id, started, narrators]);

    // Reset which intros have been shown if the user navigates away & back.
    // (Tied to the demoSlug so re-loading the page or jumping between demos
    // resets the "seen" set.)
    useEffect(() => {
        introShownRef.current = new Set();
        setIntroNarrator(null);
    }, [demoSlug]);

    async function submitEndEmail(e) {
        e.preventDefault();
        const email = endEmail.trim();
        if (!email || !email.includes("@")) {
            toast.error("Please enter a valid email.");
            return;
        }
        setEndSubmitting(true);
        try {
            await publicApi.post("/waitlist", {
                email,
                relation: endRelation || null,
                source: sourceTag,
                notes: `Joined after watching ${profile?.subject_name_short || "John"}'s full remembr session`,
            });
            setEndSaved(true);
            toast.success("You're on the list. We'll be in touch.");
        } catch (err) {
            toast.error("Something went wrong. Try again?");
        } finally {
            setEndSubmitting(false);
        }
    }

    if (loading) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${T.bg}`} data-testid="demo-loading">
                <ForgetMeNotIcon size={48} className="animate-pulse" />
            </div>
        );
    }

    // ----- INTRO OVERLAY -----
    if (!started) {
        return (
            <div className={`min-h-screen ${T.bg} ${T.text} relative overflow-hidden flex flex-col`} data-testid="demo-intro">
                <Link to="/" className={`absolute top-4 left-4 z-30 text-xs ${T.subText} hover:opacity-80 px-3 py-2 inline-flex items-center gap-1.5`} data-testid="demo-back-home">
                    <X size={14} /> {t("demo.close")}
                </Link>
                <div className="flex-1 flex items-center justify-center px-6 py-24">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        className="max-w-2xl text-center"
                    >
                        <div className="mx-auto mb-8 w-20 h-20"><ForgetMeNotIcon size={80} /></div>
                        <p className={`font-sans text-xs uppercase tracking-[0.32em] ${T.eyebrow} mb-6`}>{t("demo.intro_eyebrow")}</p>
                        <h1 className={`font-serif text-4xl sm:text-5xl lg:text-6xl ${T.text} leading-tight`}>
                            {t("demo.intro_headline_part1")} <em className={`italic ${T.titleAccent}`}>{profile?.subject_name_short || t("demo.intro_headline_who")}</em><br/> {t("demo.intro_headline_part2")}
                        </h1>
                        <p className={`mt-8 ${T.subText} leading-relaxed text-base sm:text-lg max-w-xl mx-auto`}>
                            {profile?.tagline || profile?.bio || t("demo.intro_body_1")}
                        </p>
                        <p className={`mt-4 ${T.subText} leading-relaxed text-sm sm:text-base max-w-xl mx-auto opacity-80`}>
                            {t("demo.intro_body_2_part1")} <strong className={T.text}>{t("demo.intro_body_2_bold")}</strong> {t("demo.intro_body_2_part2")}
                        </p>
                        <button
                            onClick={handleStart}
                            className={`mt-10 inline-flex items-center gap-3 px-10 py-5 rounded-full ${T.ctaBg} text-base font-semibold tracking-wide shadow-rem-glow transition-all hover:-translate-y-0.5`}
                            data-testid="demo-start"
                        >
                            <Play size={22} weight="fill" />
                            {t("demo.intro_cta")}
                        </button>
                        <p className={`mt-6 text-xs ${T.subText} opacity-60 max-w-md mx-auto`}>
                            {playlist.length || "—"} {t("demo.intro_footnote_photos")} · {t("demo.intro_footnote_tail")}
                        </p>

                        {/* Smart Shuffle controls — caregivers pick session style + length here */}
                        <SessionControls
                            style={shuffleStyle}
                            onStyleChange={setShuffleStyle}
                            capMinutes={capMinutes}
                            onCapMinutesChange={setCapMinutes}
                            estimatedSec={sessionStats.sec}
                            memoryCount={sessionStats.count}
                            theme={isKidsDemo ? "light" : "dark"}
                        />
                    </motion.div>
                </div>
            </div>
        );
    }

    if (!current) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#1a1c25] text-white/70 text-sm">
                {t("demo.not_ready")}
            </div>
        );
    }

    const atEnd = idx >= playlist.length - 1;
    // Build the photo carousel: cover photo first, then any extras from
    // photo_file_ids[]. When the album cycles, we pick the indexed entry.
    const albumPhotos = current ? [current.photo_file_id, ...(current.photo_file_ids || [])].filter(Boolean) : [];
    const switchPhotoId = photoSwitched ? current?.photo_switch?.file_id : null;
    const activePhotoId = switchPhotoId || (albumPhotos.length > 0 ? albumPhotos[albumIdx % albumPhotos.length] : null);
    const photoSrc = activePhotoId ? fileUrl(activePhotoId) : null;
    const videoSrc = current.video_file_id ? fileUrl(current.video_file_id) : null;
    // Prefer the locale-matched translated narration when the backend has one.
    // Falls back to the English audio_file_id so EN viewers (and any locale
    // we haven't yet generated audio for) still get the narration.
    // (Locale-aware text only — audioSrc itself was lifted up next to `current`
    // so the WebAudio stabiliser can subscribe to it.)
    // Locale-aware text — falls back to the EN source when no translation exists.
    const displayTitle = (current.title_translations && current.title_translations[lang]) || current.title;
    const displayStory = (current.story_translations && current.story_translations[lang]) || current.story;

    return (
        <div className={`min-h-screen ${T.bg} ${T.text} relative overflow-hidden flex flex-col`} data-testid="demo-player">
            {/* Narrator intro overlay — "Hi John, it's Margaret…" — fires the
                first time a narrator's memory comes up in a session. */}
            <AnimatePresence>
                {introNarrator && (
                    <NarratorIntroCard
                        key={introNarrator.intro_audio_file_id}
                        videoUrl={introNarrator.intro_video_file_id ? `${API}/demo/files/${introNarrator.intro_video_file_id}/download` : null}
                        photoUrl={introNarrator.intro_photo_file_id ? `${API}/demo/files/${introNarrator.intro_photo_file_id}/download` : null}
                        audioUrl={`${API}/demo/files/${introNarrator.intro_audio_file_id}/download`}
                        displayName={introNarrator.display_name}
                        relationship={introNarrator.relationship}
                        profileFirstName={profile?.subject_name_short || profile?.full_name?.split(" ")[0]}
                        theme={isKidsDemo ? "light" : "dark"}
                        onComplete={() => setIntroNarrator(null)}
                        onSkip={() => setIntroNarrator(null)}
                    />
                )}
            </AnimatePresence>

            {/* Top bar: close + "voices warming up" indicator + progress dots */}
            <Link to="/" className="absolute top-4 left-4 z-30 text-xs text-white/50 hover:text-white px-3 py-2 inline-flex items-center gap-1.5" data-testid="demo-exit">
                <X size={14} /> {t("demo.exit")}
            </Link>

            {audioReady.total > 0 && audioReady.with_audio < audioReady.total && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 px-3 py-1.5 rounded-full bg-white/10 text-[10px] uppercase tracking-[0.22em] text-white/70 backdrop-blur" data-testid="demo-warming">
                    {t("demo.warming_up")} · {audioReady.with_audio}/{audioReady.total}
                </div>
            )}

            {/* Session-mode badge — visible whenever the demo is launched in a
                non-full mode via URL query (e.g. /demo/john?mode=lights-on).
                Helps distributors visually confirm which mode is being shown. */}
            {sessionMode !== "full" && (
                <div
                    className={`absolute z-30 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[11px] uppercase tracking-[0.22em] text-white/85 ${
                        audioReady.total > 0 && audioReady.with_audio < audioReady.total
                            ? "top-12 left-1/2 -translate-x-1/2"
                            : "top-4 left-1/2 -translate-x-1/2"
                    }`}
                    data-testid="demo-mode-badge"
                >
                    {sessionMode === "today" && (<><Sun size={13} weight="fill" className="text-[#F4D068]" /> Today&apos;s session</>)}
                    {sessionMode === "themed" && (<><Sparkle size={13} weight="fill" className="text-[#A8B5E0]" /> Themed · {sessionTheme}</>)}
                    {sessionMode === "shuffle" && (<><Shuffle size={13} weight="fill" className="text-[#A8B5E0]" /> Shuffle</>)}
                    {sessionMode === "era" && (<><Hourglass size={13} weight="fill" className="text-[#D4C9B0]" /> The {sessionEra}</>)}
                    {sessionMode === "lights-on" && (<><MoonStars size={13} weight="fill" className="text-[#A8B5E0]" /> Lights On · Calm session</>)}
                </div>
            )}

            <div className="absolute top-4 right-4 z-30 flex flex-wrap justify-end gap-1 max-w-[55vw]" data-testid="demo-progress">
                {playlist.map((_, i) => (
                    <span key={i} className={`h-1 rounded-full transition-all ${playlist.length > 36
                        ? (i < idx ? "w-1.5 bg-white/40" : i === idx ? "w-4 bg-white" : "w-1.5 bg-white/15")
                        : (i < idx ? "w-3 bg-white/40" : i === idx ? "w-6 bg-white" : "w-3 bg-white/15")}`} />
                ))}
            </div>

            {/* Skip-to-next-chapter pill — for revisiting viewers who want to jump
                ahead. Subtle so first-time viewers aren't tempted to use it,
                findable for anyone who's seen the demo before. Hidden during
                the handover finale where there's nothing left to skip to. */}
            {nextChapterTarget && started && !handoverHold && (
                <motion.button
                    type="button"
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.4 }}
                    onClick={skipToNextChapter}
                    className={`absolute top-10 right-4 sm:top-11 z-30 px-3.5 py-1.5 rounded-full border text-[10px] uppercase tracking-[0.22em] backdrop-blur shadow-sm transition-all hover:-translate-y-0.5 inline-flex items-center gap-1.5 ${
                        isKidsDemo
                            ? "bg-white/90 hover:bg-white border-[#F4D5DD] text-[#D88AAB] hover:text-[#C57A9C]"
                            : "bg-white/15 hover:bg-white/25 border-white/30 text-white/85 hover:text-white"
                    }`}
                    data-testid="skip-to-next-chapter"
                    title={t("demo.skip_to_next_chapter")}
                >
                    <span>{t("demo.skip_to", { chapter: t(`demo.chapters.${nextChapterTarget.key}`, { defaultValue: nextChapterTarget.label.eyebrow }) })}</span>
                    <span aria-hidden="true">→</span>
                </motion.button>
            )}

            {/* Buffering spinner — appears subtly in the centre of the screen
                whenever the current audio is stalled waiting for network.
                Cleared the instant `onPlaying` / `onCanPlay` fires. Skipped
                during the chapter overlay or the handover hold so it never
                competes with those cinematic moments. */}
            {audioBuffering && started && !chapterOverlay && !handoverHold && (
                <div className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center" data-testid="demo-buffering-spinner">
                    <div className="w-12 h-12 rounded-full border-2 border-white/20 border-t-white/70 animate-spin" />
                </div>
            )}

            {/* Photo */}
            <div className="relative flex-1 flex items-center justify-center p-4 pt-16">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={current.id}
                        initial={{ opacity: 0, scale: 1.02 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.99 }}
                        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                        className="w-full max-w-4xl"
                    >
                        {photoSrc ? (
                            videoSrc ? (
                                // Family-uploaded MP4 (e.g. "Ella's first wobbly steps") —
                                // plays inline above the photo with sound from the audio narrator track.
                                // Muted so the narration is the dominant audio.
                                <video
                                    src={videoSrc}
                                    poster={photoSrc}
                                    autoPlay
                                    muted
                                    playsInline
                                    loop
                                    className="w-full max-h-[60vh] object-contain rounded-2xl shadow-2xl mx-auto bg-black"
                                    data-testid="demo-video"
                                />
                            ) : current.media_embed && current.media_embed.type === "youtube" ? (
                                // Tap-to-play: the photo itself becomes a play surface for memories
                                // that have a YouTube embed. Subtle Play overlay on hover/tap.
                                <button
                                    type="button"
                                    onClick={() => setEmbedOpen(true)}
                                    className="group relative w-full max-w-4xl mx-auto block rounded-2xl overflow-hidden shadow-2xl focus:outline-none focus:ring-2 focus:ring-white/40"
                                    data-testid="demo-photo-playable"
                                    aria-label={current.media_embed.label}
                                >
                                    <img
                                        src={photoSrc}
                                        alt={displayTitle}
                                        data-fid={activePhotoId}
                                        onError={handlePhotoError}
                                        className="w-full max-h-[60vh] object-contain mx-auto transition-transform duration-700 group-hover:scale-[1.02]"
                                        data-testid="demo-photo"
                                    />
                                    {/* gradient + play badge — bottom-centred so faces stay clear */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-center pb-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                                        <div className="w-16 h-16 rounded-full bg-white/95 text-[#4A5A95] flex items-center justify-center shadow-rem-glow">
                                            <Play size={28} weight="fill" />
                                        </div>
                                    </div>
                                </button>
                            ) : (
                                <motion.img key={activePhotoId} initial={{ opacity: 0.25 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, ease: "easeOut" }} src={photoSrc} alt={displayTitle} data-fid={activePhotoId} onError={handlePhotoError} className="w-full max-h-[60vh] object-contain rounded-2xl shadow-2xl mx-auto" data-testid="demo-photo" />
                            )
                        ) : (
                            <div className="w-full aspect-video bg-gradient-to-br from-[#2a3450] to-[#1a1c25] rounded-2xl flex items-center justify-center">
                                <ForgetMeNotIcon size={88} />
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>

                {/* One-shot discoverable hint — pulses softly beneath the photo for ~3s
                    on memories that have a YouTube embed. Keyed by memory.id so it
                    replays once for each new media-embed memory. */}
                {current.media_embed && current.media_embed.type === "youtube" && (
                    <motion.div
                        key={current.id + "-tap-hint"}
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: [0, 1, 1, 0], y: [-4, 0, 0, -4] }}
                        transition={{ duration: 3.0, times: [0, 0.2, 0.75, 1], ease: "easeOut" }}
                        className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-3 sm:bottom-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/55 backdrop-blur-sm border border-white/15"
                        data-testid="demo-tap-hint"
                    >
                        <span className="text-[#A8B5E0] text-base leading-none">▾</span>
                        <span className="text-white/90 text-xs sm:text-sm tracking-wide font-light">
                            Tap the photo to bring this moment to life
                        </span>
                    </motion.div>
                )}
            </div>

            {/* Title + story + contributor */}
            <div className="px-6 sm:px-10 pb-32 max-w-4xl mx-auto w-full text-center">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={current.id + "-text"}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                    >
                        <p className={`text-xs uppercase tracking-[0.3em] ${T.eyebrow} mb-2`} data-testid="demo-category">
                            {current.audio_skip ? "A remembr session" : current.category_slug.replace(/-/g, " ")}
                        </p>
                        <h2 className={`font-serif text-2xl sm:text-4xl ${T.text} leading-snug`} data-testid="demo-title">
                            {current.audio_skip
                                ? "This is how families and friends gather to create a remembr session for the loved ones they hold dear."
                                : displayTitle}
                        </h2>
                        {displayStory && !current.audio_skip && (
                            <p className={`mt-4 ${T.captionTone} leading-relaxed text-sm sm:text-base max-w-2xl mx-auto`}>
                                {displayStory}
                            </p>
                        )}
                        {current.contributor_name && (
                            <p className={`mt-4 ${T.titleAccent} italic text-sm`} data-testid="demo-contributor">
                                — {current.contributor_name}
                            </p>
                        )}
                        {current.media_embed && current.media_embed.type === "youtube" && (
                            <button
                                onClick={() => setEmbedOpen(true)}
                                className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-medium tracking-wide transition-all hover:-translate-y-0.5 backdrop-blur"
                                data-testid="demo-media-pill"
                            >
                                <FilmReel size={14} weight="fill" className="text-[#F4D068]" />
                                {current.media_embed.label}
                            </button>
                        )}
                        {(current.category_slug === "music" || current.category_slug === "movies-tv") && (
                            <button
                                onClick={() => setPlaylistTeaseOpen(true)}
                                className="mt-3 sm:mt-5 sm:ml-2 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6B7DB8]/30 hover:bg-[#6B7DB8]/50 border border-[#A8B5E0]/40 text-white text-xs font-medium tracking-wide transition-all hover:-translate-y-0.5 backdrop-blur"
                                data-testid="demo-playlist-pill"
                            >
                                <span aria-hidden>＋</span>
                                {isKidsDemo
                                    ? (current.category_slug === "music"
                                        ? `Add ${profile?.subject_name_short || "Ella"}'s lullabies & fairy tales`
                                        : `Add ${profile?.subject_name_short || "Ella"}'s favourite shows`)
                                    : (current.category_slug === "music"
                                        ? `Build ${profile?.subject_name_short || "John"}'s full playlist`
                                        : `Build ${profile?.subject_name_short || "John"}'s film & TV list`)}
                            </button>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            {audioSrc && (
                <audio
                    key={audioSrc}
                    ref={audioRef}
                    src={audioSrc}
                    preload="auto"
                    onEnded={() => {
                        // On the handover memory, intercept the natural audio end
                        // with a 3-second fade-to-black typographic hold before
                        // the email-capture card becomes the visual centre.
                        if (atEnd && current?.category_slug === "to-be-continued") {
                            showHandoverHold();
                        } else if (atEnd) {
                            // Non-handover final memory — reveal the CTA as the
                            // closing narration ends (no fade-to-black).
                            setEndCtaVisible(true);
                        }
                        advance();
                    }}
                    onError={() => {
                        // Broken/unreachable audio — almost always a transient
                        // network failure (preview server waking, wifi blip).
                        // Retry with backoff; only skip the memory after three
                        // failed attempts. Insta-skipping here used to chain
                        // through every remaining memory during an outage.
                        const attempt = audioRetryRef.current;
                        if (attempt < 3) {
                            audioRetryRef.current = attempt + 1;
                            setAudioBuffering(true);
                            setTimeout(() => {
                                const a = audioRef.current;
                                if (!a) return;
                                try { a.load(); if (!paused) a.play().catch(() => {}); } catch (_) { /* retry again on next error */ }
                            }, 1500 * (attempt + 1));
                        } else {
                            advance();
                        }
                    }}
                    onWaiting={() => setAudioBuffering(true)}
                    onPlaying={() => { setAudioBuffering(false); audioRetryRef.current = 0; }}
                    onCanPlay={() => setAudioBuffering(false)}
                    onTimeUpdate={(e) => {
                        const a = e.currentTarget;
                        // Timed photo switch (one-way) when the narration
                        // reaches the configured moment.
                        const sw = current?.photo_switch;
                        if (sw?.file_id && !photoSwitched && (a.currentTime || 0) >= (sw.at_seconds || 0)) {
                            setPhotoSwitched(true);
                        }
                        // Trigger the chapter-transition overlay during the final
                        // ~4s of audio whenever the next memory opens a new
                        // chapter (chapter-two, chapter-three, handover, …).
                        if (!nextChapterKey) return;
                        const remaining = (a.duration || 0) - (a.currentTime || 0);
                        if (remaining > 0 && remaining < 4.2 && !chapterOverlay) {
                            setChapterOverlay(CHAPTER_LABELS[nextChapterKey]);
                        }
                    }}
                    data-testid="demo-audio"
                />
            )}

            {/* Chapter-transition overlay — fades in over the last 4 seconds */}
            <AnimatePresence>
                {chapterOverlay && (
                    <motion.div
                        key="chapter-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1.4, ease: "easeInOut" }}
                        className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none px-6"
                        data-testid="chapter-overlay"
                    >
                        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/75 to-black/60" />
                        <motion.div
                            initial={{ y: 24, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 1.6, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                            className="relative text-center max-w-2xl"
                        >
                            <p className="font-sans text-[11px] uppercase tracking-[0.42em] text-[#F4C7CC]/80 mb-5" data-testid="chapter-overlay-eyebrow">
                                {chapterOverlay.eyebrow}
                            </p>
                            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white italic leading-tight" data-testid="chapter-overlay-title">
                                {chapterOverlay.title}
                            </h2>
                            <p className="mt-5 font-sans text-xs tracking-[0.32em] text-white/55 uppercase" data-testid="chapter-overlay-years">
                                {chapterOverlay.years}
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Handover fade-to-black hold — 3 second cinematic finale on the
                very last memory of the handover chapter, gates the email-capture
                CTA so it doesn't snap in over the closing narration. */}
            <AnimatePresence>
                {handoverHold && (
                    <motion.div
                        key="handover-hold"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1.4, ease: "easeInOut" }}
                        className="absolute inset-0 z-50 flex items-center justify-center bg-black"
                        data-testid="handover-hold"
                    >
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 1.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            className="text-center max-w-2xl px-6"
                        >
                            <p className="font-sans text-[11px] uppercase tracking-[0.42em] text-[#F4C7CC]/85 mb-6">
                                The handover
                            </p>
                            <h2 className="font-serif text-3xl sm:text-5xl text-white italic leading-tight" data-testid="handover-hold-line">
                                {handoverHold}
                            </h2>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* End-of-demo CTA — DISABLED for the public marketing demo
                (Feb 2026, per Glenn's direct request: "no session features
                page prompts to cut into both Johns and Ella's demos…in demo
                on site this is simply destroying the emotional flow").
                The "Sessions that scale" / "Press play again next Saturday"
                explainer + email-capture card kept appearing in places that
                broke the emotional thread (reported at end of Melbourne Cup
                memory). For the public demo we now flow uninterrupted from
                the opening establishing frame all the way to the final
                memory's photo, then leave the photo on screen so the visitor
                can simply press the "end demo" link in the top-left.
                The same CTA still lives in the authenticated player
                (ReminiscePlayer.jsx) for paying families. */}
            {false && atEnd && endCtaVisible && null}

            {/* Compact tablet-friendly controls — sized down so they don't crowd the contributor caption */}
            <div className={`absolute bottom-0 inset-x-0 z-30 bg-gradient-to-t ${T.gradientFrom} via-transparent to-transparent pt-6 pb-4`}>
                <div className="flex items-center justify-center gap-3 sm:gap-5">
                    <button
                        onClick={goBack}
                        disabled={idx === 0}
                        className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full ${isKidsDemo ? "bg-white/70 hover:bg-white" : "bg-white/10 hover:bg-white/20"} disabled:opacity-30 flex items-center justify-center backdrop-blur transition-all hover:-translate-y-0.5 ${isKidsDemo ? T.controlText : ""}`}
                        data-testid="demo-back"
                        aria-label="Previous memory"
                    >
                        <SkipBack size={18} weight="fill" />
                    </button>
                    <button
                        onClick={() => setPaused((p) => !p)}
                        className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full ${T.controlBg} ${T.controlText} ${T.controlHoverBg} shadow-rem-glow flex items-center justify-center transition-all hover:-translate-y-0.5`}
                        data-testid="demo-toggle"
                        aria-label={paused ? "Resume" : "Pause"}
                    >
                        {paused ? <Play size={24} weight="fill" /> : <Pause size={24} weight="fill" />}
                    </button>
                    <button
                        onClick={advance}
                        disabled={atEnd}
                        className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full ${isKidsDemo ? "bg-white/70 hover:bg-white" : "bg-white/10 hover:bg-white/20"} disabled:opacity-30 flex items-center justify-center backdrop-blur transition-all hover:-translate-y-0.5 ${isKidsDemo ? T.controlText : ""}`}
                        data-testid="demo-next"
                        aria-label="Next memory"
                    >
                        <SkipForward size={18} weight="fill" />
                    </button>
                </div>
                <div className="mt-4 flex justify-center px-4">
                    <VolumeControl volume={volume} preset={preset} setVolume={setVolume} setPreset={setPreset} theme={isKidsDemo ? "light" : "dark"} />
                </div>
            </div>

            {/* YouTube media-embed modal */}
            <AnimatePresence>
                {embedOpen && current.media_embed && current.media_embed.type === "youtube" && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
                        onClick={() => setEmbedOpen(false)}
                        data-testid="demo-media-modal"
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            className="relative w-full max-w-4xl"
                        >
                            {/* High-contrast labelled close — sits above the frame so it
                                never blends into the player chrome (user feedback). */}
                            <button
                                onClick={() => setEmbedOpen(false)}
                                className="absolute -top-12 right-0 z-10 inline-flex items-center gap-2 rounded-full bg-white text-[#1a1c25] hover:bg-white/90 hover:scale-[1.03] active:scale-95 px-5 py-2.5 text-sm font-bold uppercase tracking-wide shadow-xl transition-all"
                                data-testid="demo-media-close"
                                aria-label="Close video"
                            >
                                <X size={16} weight="bold" /> {t("demo.close")}
                            </button>
                            <div className="bg-[#1a1c25] rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                                <div className="aspect-video bg-black">
                                    <iframe
                                        title={current.media_embed.label}
                                        src={`https://www.youtube.com/embed/${current.media_embed.youtube_id}?rel=0&modestbranding=1`}
                                        className="w-full h-full"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                        data-testid="demo-media-iframe"
                                    />
                                </div>
                                {current.media_embed.caption && (
                                    <div className="p-5 sm:p-6">
                                        <p className="text-white/80 text-sm leading-relaxed italic font-serif">
                                            {current.media_embed.caption}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* "Build the full playlist" — REAL upload form */}
            <AnimatePresence>
                {playlistTeaseOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
                        onClick={() => { if (!playlistSubmitting) setPlaylistTeaseOpen(false); }}
                        data-testid="demo-playlist-modal"
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            className="relative w-full max-w-md bg-[#1f2230] rounded-3xl border border-white/10 shadow-2xl p-7 sm:p-9"
                        >
                            <button
                                onClick={() => { if (!playlistSubmitting) setPlaylistTeaseOpen(false); }}
                                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all"
                                data-testid="demo-playlist-close"
                                aria-label="Close"
                            >
                                <X size={18} weight="bold" />
                            </button>
                            {!playlistSaved ? (
                                <>
                                    <div className="mx-auto mb-4 w-14 h-14"><ForgetMeNotIcon size={56} /></div>
                                    <p className="font-sans text-[10px] uppercase tracking-[0.32em] text-[#A8B5E0] mb-3 text-center">
                                        Build the full collection
                                    </p>
                                    <h3 className="font-serif text-2xl sm:text-[26px] text-white leading-snug text-center">
                                        {current?.category_slug === "music"
                                            ? "Drop the playlist"
                                            : "Drop the film & TV list"}
                                    </h3>
                                    <p className="mt-3 text-sm text-white/70 leading-relaxed font-light text-center">
                                        {current?.category_slug === "music"
                                            ? "Paste a Spotify, Apple Music or YouTube playlist URL — every song your loved one cherished, queued up for their session."
                                            : "Paste a YouTube playlist, an IMDb list, or a Letterboxd list URL — every film and TV show, ready for their cinema."}
                                    </p>
                                    <form
                                        onSubmit={async (e) => {
                                            e.preventDefault();
                                            if (playlistSubmitting) return;
                                            setPlaylistError(null);
                                            setPlaylistSubmitting(true);
                                            try {
                                                const res = await fetch(`${API}/playlist-requests`, {
                                                    method: "POST",
                                                    headers: { "Content-Type": "application/json" },
                                                    body: JSON.stringify({
                                                        demo_slug: demoSlug,
                                                        memory_id: current.id,
                                                        list_type: current.category_slug,
                                                        list_url: playlistUrl.trim(),
                                                        list_name: playlistName.trim() || null,
                                                        contributor_email: playlistEmail.trim() || null,
                                                    }),
                                                });
                                                if (!res.ok) {
                                                    const d = await res.json().catch(() => ({}));
                                                    throw new Error(d.detail || "Couldn't save — please try again.");
                                                }
                                                setPlaylistSaved(true);
                                            } catch (err) {
                                                setPlaylistError(err.message);
                                            } finally {
                                                setPlaylistSubmitting(false);
                                            }
                                        }}
                                        className="mt-5 space-y-3"
                                    >
                                        <input
                                            type="url"
                                            required
                                            placeholder={current?.category_slug === "music"
                                                ? "https://open.spotify.com/playlist/…"
                                                : "https://www.youtube.com/playlist?list=…"}
                                            value={playlistUrl}
                                            onChange={(e) => setPlaylistUrl(e.target.value)}
                                            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 focus:border-[#A8B5E0]/60 focus:outline-none text-white placeholder:text-white/30 text-sm transition-colors"
                                            data-testid="demo-playlist-url"
                                            autoFocus
                                        />
                                        <input
                                            type="text"
                                            placeholder={current?.category_slug === "music" ? "Playlist name (optional)" : "List name (optional)"}
                                            value={playlistName}
                                            onChange={(e) => setPlaylistName(e.target.value)}
                                            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 focus:border-[#A8B5E0]/60 focus:outline-none text-white placeholder:text-white/30 text-sm transition-colors"
                                            data-testid="demo-playlist-name"
                                        />
                                        <input
                                            type="email"
                                            placeholder="Your email (optional — so we can ping you when it's live)"
                                            value={playlistEmail}
                                            onChange={(e) => setPlaylistEmail(e.target.value)}
                                            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 focus:border-[#A8B5E0]/60 focus:outline-none text-white placeholder:text-white/30 text-sm transition-colors"
                                            data-testid="demo-playlist-email"
                                        />
                                        {playlistError && (
                                            <p className="text-[#F4A3A3] text-xs">{playlistError}</p>
                                        )}
                                        <button
                                            type="submit"
                                            disabled={playlistSubmitting || !playlistUrl.trim()}
                                            className="w-full py-3 rounded-xl bg-[#4A5A95] hover:bg-[#5A6BA9] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium tracking-wide transition-all"
                                            data-testid="demo-playlist-submit"
                                        >
                                            {playlistSubmitting ? "Saving…" : "Save the list"}
                                        </button>
                                        <p className="text-xs text-white/40 text-center font-light leading-relaxed pt-1">
                                            We'll have your collection queued up the day remembr opens. No spam, ever.
                                        </p>
                                    </form>
                                </>
                            ) : (
                                <div className="text-center py-4">
                                    <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-[#4A5A95]/20 border border-[#A8B5E0]/30 flex items-center justify-center">
                                        <CheckCircle size={36} weight="fill" className="text-[#A8B5E0]" />
                                    </div>
                                    <h3 className="font-serif text-2xl text-white">Saved. Beautifully.</h3>
                                    <p className="mt-3 text-sm text-white/70 leading-relaxed font-light">
                                        Your collection is now part of the record. The moment remembr opens to families, it'll be queued up — every song, every film — ready for your loved one.
                                    </p>
                                    <button
                                        onClick={() => {
                                            setPlaylistTeaseOpen(false);
                                            // reset after the modal animates out
                                            setTimeout(() => {
                                                setPlaylistSaved(false);
                                                setPlaylistUrl("");
                                                setPlaylistName("");
                                                setPlaylistEmail("");
                                                setPlaylistError(null);
                                            }, 400);
                                        }}
                                        className="mt-6 inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs uppercase tracking-[0.22em] transition-all"
                                        data-testid="demo-playlist-done"
                                    >
                                        {t("demo.end.back_to_story", { name: profile?.subject_name_short || "John" })}
                                    </button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
